/**
 * Finding a receipt by its outline, for when brightness alone cannot: white paper on a light desk
 * is barely brighter than the desk, but its four sides are long, straight edges. Document scanner
 * apps work the same way:
 *
 * 1. Edges: the photo is smoothed and its brightness gradient measured (Sobel).
 * 2. Lines: every edge pixel with plain surface on its darker side votes for the straight lines it
 *    could lie on, in the direction its gradient points (a Hough transform); the strongest lines
 *    per direction are kept, plus the photo's own borders for a receipt that runs out of the frame.
 * 3. The frame: two roughly parallel lines and two more across them make a quadrilateral. Each
 *    side scores by how much of it is an edge with the brighter side inward (paper is brighter than
 *    what it lies on) and nothing but plain surface just outside. Print has edges of both kinds
 *    side by side, so a line through the text never passes for the receipt's border; the desk
 *    beside the paper is plain. The best frame wins, larger and central frames preferred.
 *
 * Tuned on real photos of receipts on a light desk next to a book and cables; change thresholds
 * against such photos, not guesses.
 *
 * Pure, on raw pixels, shared by the phone and services/ai like geometry.ts.
 */
import {
  detectReceipt,
  orderCorners,
  polygonArea,
  type Detection,
  type Point,
  type Quad,
  type RawImage,
} from "./geometry";

/** Angle steps of the line search, in degrees. */
const ANGLE_STEP = 1;
/**
 * Lines kept from the vote: the strongest few per direction (15 degree buckets). A receipt's top is
 * short and faint next to a cable or a book, but it only has to beat the lines running its way.
 */
const PER_DIRECTION = 6;
const DIRECTION_DEGREES = 15;
/** A frame side counts as found where this share of it has an inward edge with a plain outside. */
// The outline of white paper on a light desk is faint and broken; a side needs a fifth of its
// length, a frame a third on average (the real receipts in the tuning set score 0.3 - 0.7).
const MIN_SIDE = 0.2;
const MIN_MEAN = 0.35;
/** Smallest share of the photo the receipt may take; it is the subject. */
const MIN_COVERAGE = 0.08;
/** What an image-border side scores: it may be where the receipt runs out, it is not evidence. */
const BORDER_SUPPORT = 0.4;

type Line = { theta: number; rho: number; votes: number; border?: boolean };
type Gradient = { gx: Float32Array; gy: Float32Array; magnitude: Float32Array; threshold: number };

function grayOf(image: RawImage): Float32Array {
  const { data, width, height, channels } = image;
  const out = new Float32Array(width * height);
  for (let i = 0; i < out.length; i += 1) {
    const o = i * channels;
    out[i] =
      channels < 3 ? data[o]! : 0.299 * data[o]! + 0.587 * data[o + 1]! + 0.114 * data[o + 2]!;
  }
  return out;
}

/** A box blur, run twice by the caller: close enough to a Gaussian, and it ignores paper grain. */
function boxBlur(values: Float32Array, width: number, height: number, radius: number) {
  const pass = (input: Float32Array, horizontal: boolean) => {
    const out = new Float32Array(input.length);
    const lines = horizontal ? height : width;
    const length = horizontal ? width : height;
    for (let line = 0; line < lines; line += 1) {
      const at = (i: number) => (horizontal ? line * width + i : i * width + line);
      let sum = 0;
      for (let i = -radius; i <= radius; i += 1)
        sum += input[at(Math.min(length - 1, Math.max(0, i)))]!;
      for (let i = 0; i < length; i += 1) {
        out[at(i)] = sum / (2 * radius + 1);
        sum +=
          input[at(Math.min(length - 1, i + radius + 1))]! - input[at(Math.max(0, i - radius))]!;
      }
    }
    return out;
  };
  return pass(pass(values, true), false);
}

function gradient(gray: Float32Array, width: number, height: number): Gradient {
  const gx = new Float32Array(gray.length);
  const gy = new Float32Array(gray.length);
  const magnitude = new Float32Array(gray.length);
  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const i = y * width + x;
      const tl = gray[i - width - 1]!;
      const t = gray[i - width]!;
      const tr = gray[i - width + 1]!;
      const l = gray[i - 1]!;
      const r = gray[i + 1]!;
      const bl = gray[i + width - 1]!;
      const b = gray[i + width]!;
      const br = gray[i + width + 1]!;
      gx[i] = tr + 2 * r + br - tl - 2 * l - bl;
      gy[i] = bl + 2 * b + br - tl - 2 * t - tr;
      magnitude[i] = Math.hypot(gx[i]!, gy[i]!);
    }
  }
  // An edge is clearly above the photo's grain. White paper on a light desk is a faint edge
  // (about 15 here, where black print reaches 200), so the bar sits just over the typical gradient.
  const sorted = Float32Array.from(magnitude).sort();
  const threshold = Math.max(8, 2 * sorted[Math.floor(sorted.length * 0.5)]!);
  return { gx, gy, magnitude, threshold };
}

/** The strongest straight lines: a Hough vote in which each edge pixel votes along its gradient. */
function houghLines(g: Gradient, width: number, height: number): Line[] {
  const angles = Math.round(180 / ANGLE_STEP);
  const diagonal = Math.ceil(Math.hypot(width, height));
  const rhos = 2 * diagonal + 1;
  const votes = new Float32Array(angles * rhos);
  const cos = new Float32Array(angles);
  const sin = new Float32Array(angles);
  for (let a = 0; a < angles; a += 1) {
    cos[a] = Math.cos((a * ANGLE_STEP * Math.PI) / 180);
    sin[a] = Math.sin((a * ANGLE_STEP * Math.PI) / 180);
  }
  const spread = Math.round(3 / ANGLE_STEP);
  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const i = y * width + x;
      const m = g.magnitude[i]!;
      if (m < g.threshold) continue;
      // Only edges with a plain darker side vote: the paper's outline has the desk beside it,
      // print has more print, so lines of text never crowd out the receipt's short top edge.
      if (!clearOutside(g, width, x, y, -g.gx[i]! / m, -g.gy[i]! / m)) continue;
      // The line's normal is the gradient: vote only for angles near it.
      let angle = (Math.atan2(g.gy[i]!, g.gx[i]!) * 180) / Math.PI;
      if (angle < 0) angle += 180;
      if (angle >= 180) angle -= 180;
      const centre = Math.round(angle / ANGLE_STEP);
      for (let d = -spread; d <= spread; d += 1) {
        const a = (centre + d + angles) % angles;
        const rho = Math.round(x * cos[a]! + y * sin[a]!) + diagonal;
        votes[a * rhos + rho]! += 1;
      }
    }
  }

  // Peaks, strongest first, each suppressing its neighbourhood so one edge is one line.
  const minVotes = Math.min(width, height) * 0.05;
  const peaks: Line[] = [];
  const order = Array.from(votes.keys()).filter((k) => votes[k]! >= minVotes);
  order.sort((p, q) => votes[q]! - votes[p]!);
  const taken: Array<{ a: number; r: number }> = [];
  const perDirection = new Map<number, number>();
  for (const k of order) {
    const a = Math.floor(k / rhos);
    const r = k % rhos;
    const direction = Math.floor((a * ANGLE_STEP) / DIRECTION_DEGREES);
    if ((perDirection.get(direction) ?? 0) >= PER_DIRECTION) continue;
    const near = taken.some(({ a: ta, r: tr }) => {
      const da = Math.min(Math.abs(ta - a), angles - Math.abs(ta - a));
      // Lines a few degrees and pixels apart are the same edge (or the paper's two faces).
      return da <= 4 / ANGLE_STEP && Math.abs(tr - r) <= 12;
    });
    if (near) continue;
    taken.push({ a, r });
    perDirection.set(direction, (perDirection.get(direction) ?? 0) + 1);
    peaks.push({ theta: (a * ANGLE_STEP * Math.PI) / 180, rho: r - diagonal, votes: votes[k]! });
  }
  return peaks;
}

function intersect(a: Line, b: Line): Point | null {
  const det = Math.cos(a.theta) * Math.sin(b.theta) - Math.sin(a.theta) * Math.cos(b.theta);
  if (Math.abs(det) < 1e-6) return null;
  return {
    x: (a.rho * Math.sin(b.theta) - b.rho * Math.sin(a.theta)) / det,
    y: (b.rho * Math.cos(a.theta) - a.rho * Math.cos(b.theta)) / det,
  };
}

/** Angle between two lines, 0..90 degrees. */
function between(a: Line, b: Line): number {
  const d = (Math.abs(a.theta - b.theta) * 180) / Math.PI;
  return Math.min(d, 180 - d);
}

/**
 * Where along a line its edge is, as prefix sums: for each pixel step t along the line, whether
 * an edge within two pixels across has its brighter side towards the line's normal (`plus`) or
 * away from it (`minus`). A frame side is then a stretch of t, scored in constant time.
 */
type Profile = { start: number; plus: Uint32Array; minus: Uint32Array };

function profile(g: Gradient, width: number, height: number, line: Line): Profile {
  const nx = Math.cos(line.theta);
  const ny = Math.sin(line.theta);
  // Foot of the line from the origin, and its direction.
  const fx = nx * line.rho;
  const fy = ny * line.rho;
  const dx = -ny;
  const dy = nx;
  const reach = Math.ceil(Math.hypot(width, height));
  const start = -reach;
  const plus = new Uint32Array(2 * reach + 2);
  const minus = new Uint32Array(2 * reach + 2);
  for (let t = -reach; t <= reach; t += 1) {
    const bx = fx + dx * t;
    const by = fy + dy * t;
    let up = 0;
    let down = 0;
    if (bx >= 1 && by >= 1 && bx < width - 1 && by < height - 1) {
      // Three pixels either side: paper that is not quite flat still counts as one edge.
      for (let k = -3; k <= 3 && !(up && down); k += 1) {
        const x = Math.round(bx + nx * k);
        const y = Math.round(by + ny * k);
        if (x < 1 || y < 1 || x >= width - 1 || y >= height - 1) continue;
        const i = y * width + x;
        const m = g.magnitude[i]!;
        if (m < g.threshold) continue;
        const along = (g.gx[i]! * nx + g.gy[i]! * ny) / m;
        // Brighter towards +n: the paper is on that side, so the outside is -n.
        if (along > 0.7 && clearOutside(g, width, x, y, -nx, -ny)) up = 1;
        else if (along < -0.7 && clearOutside(g, width, x, y, nx, ny)) down = 1;
      }
    }
    const k = t - start;
    plus[k + 1] = plus[k]! + up;
    minus[k + 1] = minus[k]! + down;
  }
  return { start, plus, minus };
}

/**
 * Whether the strip just outside an edge point is plain: no other edge from 4 to 10 pixels out.
 * The receipt's outline has the desk beside it; a line through its print has more print beside it.
 */
function clearOutside(g: Gradient, width: number, x: number, y: number, ox: number, oy: number) {
  for (let k = 4; k <= 10; k += 1) {
    const px = Math.round(x + ox * k);
    const py = Math.round(y + oy * k);
    const i = py * width + px;
    if (px < 1 || py < 1 || px >= width - 1 || i >= g.magnitude.length) return true;
    if (g.magnitude[i]! >= g.threshold) return false;
  }
  return true;
}

/** Position of a point along a line, in the units of its profile. */
const along = (line: Line, p: Point) => -Math.sin(line.theta) * p.x + Math.cos(line.theta) * p.y;

/** Share of the side from p to q on `line` that is an edge with the brighter side towards `inside`. */
function sideSupport(line: Line, profileOf: Profile, p: Point, q: Point, inside: Point): number {
  const inward = inside.x * Math.cos(line.theta) + inside.y * Math.sin(line.theta) - line.rho > 0;
  const counts = inward ? profileOf.plus : profileOf.minus;
  const t1 = Math.round(Math.min(along(line, p), along(line, q))) - profileOf.start;
  const t2 = Math.round(Math.max(along(line, p), along(line, q))) - profileOf.start;
  const lo = Math.max(0, t1);
  const hi = Math.min(counts.length - 1, t2 + 1);
  if (hi - lo < 4) return 0;
  return (counts[hi]! - counts[lo]!) / (hi - lo);
}

type Frame = { corners: Quad; score: number; sides: number[]; coverage: number };

/** Every frame that passes, best first. */
function frames(image: RawImage): Frame[] {
  const { width, height } = image;
  const smooth = (() => {
    const gray = grayOf(image);
    const radius = Math.max(1, Math.round(Math.min(width, height) / 160));
    return boxBlur(boxBlur(gray, width, height, radius), width, height, radius);
  })();
  const g = gradient(smooth, width, height);
  const lines = houghLines(g, width, height);
  // The photo's borders, for a receipt that runs out of the frame.
  const borders: Line[] = [
    { theta: 0, rho: 0, votes: 0, border: true },
    { theta: 0, rho: width - 1, votes: 0, border: true },
    { theta: Math.PI / 2, rho: 0, votes: 0, border: true },
    { theta: Math.PI / 2, rho: height - 1, votes: 0, border: true },
  ];
  const all = [...lines, ...borders];

  const profiles = new Map<Line, Profile>(
    lines.map((line) => [line, profile(g, width, height, line)] as const),
  );
  // Signed distance of the photo's centre from a line, with the normal turned to agree with `to`.
  const mid = { x: width / 2, y: height / 2 };
  const offset = (line: Line, to: Line) => {
    const flip = Math.cos(line.theta - to.theta) < 0 ? -1 : 1;
    return flip * (mid.x * Math.cos(line.theta) + mid.y * Math.sin(line.theta) - line.rho);
  };

  // Pairs of roughly parallel lines far enough apart to hold a receipt between them.
  const pairs: Array<[Line, Line]> = [];
  for (let i = 0; i < all.length; i += 1)
    for (let j = i + 1; j < all.length; j += 1) {
      const a = all[i]!;
      const b = all[j]!;
      if (between(a, b) > 20) continue;
      if (Math.abs(offset(a, a) - offset(b, a)) < Math.min(width, height) * 0.08) continue;
      pairs.push([a, b]);
    }

  const found: Frame[] = [];
  const area = width * height;
  for (let i = 0; i < pairs.length; i += 1) {
    for (let j = i + 1; j < pairs.length; j += 1) {
      const [a1, a2] = pairs[i]!;
      const [b1, b2] = pairs[j]!;
      if (between(a1, b1) < 45) continue;
      // The receipt has to show itself: at most one side on the photo's border, or two opposite
      // ones for a long receipt photographed across its whole height (or width). Two borders
      // meeting at a corner would make any object at the photo's edge a receipt.
      const borders = [a1, a2, b1, b2].filter((line) => line.border).length;
      if (borders > 2 || (borders === 2 && !(a1.border && a2.border) && !(b1.border && b2.border)))
        continue;
      const points = [intersect(a1, b1), intersect(a1, b2), intersect(a2, b2), intersect(a2, b1)];
      if (points.some((p) => !p || p.x < -2 || p.y < -2 || p.x > width + 1 || p.y > height + 1))
        continue;
      const quad = points as Quad;
      if (!convex(quad)) continue;
      const coverage = polygonArea(quad) / area;
      if (coverage < MIN_COVERAGE || coverage > 0.97) continue;
      const centre = {
        x: quad.reduce((s, p) => s + p.x, 0) / 4,
        y: quad.reduce((s, p) => s + p.y, 0) / 4,
      };
      // The sides in order: a1 between b1 and b2, b2 between a1 and a2, and so on.
      const sides: Array<[Line, Point, Point]> = [
        [a1, quad[0], quad[1]],
        [b2, quad[1], quad[2]],
        [a2, quad[2], quad[3]],
        [b1, quad[3], quad[0]],
      ];
      const scores = sides.map(([line, p, q]) =>
        line.border ? BORDER_SUPPORT : sideSupport(line, profiles.get(line)!, p, q, centre),
      );
      if (Math.min(...scores) < MIN_SIDE) continue;
      const mean = scores.reduce((s, v) => s + v, 0) / 4;
      if (mean < MIN_MEAN) continue;
      // Among frames that fit about as well, the larger one: the receipt, not the block under a
      // dashed line on it. And the one in the middle of the photo, where the receipt was aimed at.
      const score =
        mean +
        0.3 * Math.sqrt(coverage) +
        (contains(quad, { x: width / 2, y: height / 2 }) ? 0.1 : 0);
      found.push({ corners: orderCorners(quad), score, sides: scores, coverage });
    }
  }
  return found.sort((p, q) => q.score - p.score);
}

export function detectByEdges(image: RawImage): Detection | null {
  const { width, height } = image;
  const best = frames(image)[0];
  if (!best) return null;
  const clamp = (p: Point) => ({
    x: Math.min(width - 1, Math.max(0, p.x)),
    y: Math.min(height - 1, Math.max(0, p.y)),
  });
  const corners = best.corners.map(clamp) as Quad;
  return { corners, coverage: polygonArea(corners) / (width * height) };
}

/** Whether a point lies inside a convex quad. */
function contains(quad: Quad, point: Point): boolean {
  let sign = 0;
  for (let k = 0; k < 4; k += 1) {
    const a = quad[k]!;
    const b = quad[(k + 1) % 4]!;
    const cross = (b.x - a.x) * (point.y - a.y) - (b.y - a.y) * (point.x - a.x);
    if (sign === 0) sign = Math.sign(cross);
    else if (Math.sign(cross) !== sign) return false;
  }
  return true;
}

function convex(quad: Quad): boolean {
  let sign = 0;
  for (let k = 0; k < 4; k += 1) {
    const a = quad[k]!;
    const b = quad[(k + 1) % 4]!;
    const c = quad[(k + 2) % 4]!;
    const cross = (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
    if (Math.abs(cross) < 1e-9) return false;
    if (sign === 0) sign = Math.sign(cross);
    else if (Math.sign(cross) !== sign) return false;
  }
  return true;
}

/**
 * The receipt in a (downscaled) photo: by its outline first, by its brightness when no outline
 * stands out (detectReceipt in geometry.ts); null when neither finds it, and the whole photo is kept.
 */
export function findReceipt(image: RawImage): Detection | null {
  return detectByEdges(image) ?? detectReceipt(image);
}
